import { backendApi } from "@/services/api";
import {
  DeliveryViewModel,
  Sample,
  SampleSet,
} from "@/services/backend/sample-set/sample-set";
import { Filter } from "@/shared/lib/filter/filter";
import { AirSampler } from "../airsamplers/air-sampler";
import { ProcedureType, SampleType } from "../types";

const sampleSetApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    putSampleSet: build.mutation<SampleSet, PutRequest>({
      query: (request) => ({
        url: `/inspections/sampleset`,
        method: "PUT",
        body: request,
      }),
      invalidatesTags: ["SampleSet", "SampleSetDetail"],
    }),
    updateSample: build.mutation<Sample, UpdateSampleRequest>({
      query: (request) => ({
        url: `/inspections/sampleset/sample`,
        method: "PUT",
        body: request,
      }),
      invalidatesTags: ["SampleSet"],
    }),
    updateSamples: build.mutation<SampleList, UpdateSamplesRequest>({
      query: (request) => ({
        url: `/inspections/sampleset/samples`,
        method: "PUT",
        body: request,
      }),
      invalidatesTags: ["SampleSet", "SampleSetDetail"],
    }),
    setDifferentiation: build.mutation<SampleSet, SetDifferentiationRequest>({
      query: (request) => ({
        url: `/inspections/sampleset/differentiation`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: ["SampleSet", "SampleSetDetail", "Rules"],
    }),
    sampleSetList: build.query<List, ListRequest>({
      query: (request) => ({
        url: `/inspections/sampleset`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: ["SampleSet"],
    }),
    getSampleSet: build.query<SampleSet, GetRequest>({
      query: (request) => ({
        url: `/inspections/sampleset/${request.id}`,
        method: "GET",
      }),
      providesTags: ["SampleSetDetail"],
    }),
    listDeliveryViewModel: build.query<
      ListDeliveryViewModel,
      ListDeliveryViewModelRequest
    >({
      query: (request) => ({
        url: `/inspections/sampleset/list-delivery-view-model`,
        method: "GET",
        params: {
          deliveryId: request.deliveryId,
          ownerId: request.ownerId,
          ownerType: request.ownerType,
        },
      }),
      providesTags: ["SampleSet"],
    }),
  }),
});

export const {
  useGetSampleSetQuery,
  useSampleSetListQuery,
  useListDeliveryViewModelQuery,
  usePutSampleSetMutation,
  useUpdateSampleMutation,
  useUpdateSamplesMutation,
  useSetDifferentiationMutation,
} = sampleSetApi;

export interface SetDifferentiationRequest {
  id: string;
  hasDifferentiation: boolean;
}

export interface PutRequest {
  id: string;
  customerId: string;
  deliveryId: string;
  procedureType: ProcedureType;
  referenceId?: string;
  sampleSetNumber?: string;
  comment?: string;
  externalComment?: string;
  sampleType?: SampleType;
  airSampler?: AirSampler;
}

export interface PutCommentRequest {
  id: string;
  comment: string;
}

export interface PutCommentResponse {}

export interface GetRequest {
  id: string;
}

export interface DeleteRequest {
  id: string;
}

export interface ListByProcessRequest {
  processId: string;
  sampleType: SampleType;
  ownerId?: string;
  ownerType?: string;
}

export interface ListByDeliveryRequest {
  deliveryId: string;
  ownerId?: string;
  ownerType?: string;
}

export interface ListDeliveryViewModelRequest {
  deliveryId: string;
  ownerId?: string;
  ownerType?: string;
}

export interface ListDeliveryViewModel {
  count: number;
  data: DeliveryViewModel[];
}

export interface ListRequest {
  deliveryId?: Filter<string>;
  customerId?: Filter<string>;
  referenceId?: Filter<string>;
  ownerId?: Filter<string>;
  ownerType?: Filter<string>;
  sampleType?: Filter<SampleType>;
  procedureType?: Filter<ProcedureType>;
  limit: number;
  offset: number;
}

export interface List {
  count: number;
  items: SampleSet[];
}

export interface UpdateSampleRequest {
  sampleSetId: string;
  sample: Sample;
}

export interface UpdateSamplesRequest {
  samples: UpdateSampleRequest[];
}

export interface SampleList {
  count: number;
  items: Sample[];
}
